"use client";

import { useRouter } from "next/navigation";
import { useTransition } from "react";
import { LANG_COOKIE, LANG_COOKIE_MAX_AGE } from "@/lib/i18n";
import { useLang } from "./LocaleProvider";

/**
 * Footer language toggle. Sets a cookie + router.refresh() so server
 * components re-render with the new lang. The client provider receives
 * the new lang via the re-rendered layout, so no full reload required.
 *
 * Visual: small pill that shows the OTHER language as the action target,
 * e.g. when currently EN it reads "Español →", click takes you to ES.
 */
export function LanguageToggle({
  className,
}: {
  className?: string;
}) {
  const lang = useLang();
  const router = useRouter();
  const [pending, startTransition] = useTransition();

  function toggle() {
    const next = lang === "es" ? "en" : "es";
    document.cookie = `${LANG_COOKIE}=${next}; path=/; max-age=${LANG_COOKIE_MAX_AGE}; SameSite=Lax`;
    startTransition(() => {
      router.refresh();
    });
  }

  const label = lang === "es" ? "English" : "Español";

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={pending}
      aria-label={
        lang === "es"
          ? "Cambiar al inglés"
          : "Switch to Spanish"
      }
      className={
        className ??
        "mono-strip inline-flex items-center gap-2 rounded-sm border border-[var(--color-bone)]/30 px-3 py-1.5 text-[var(--color-bone)] transition hover:border-[var(--color-foil)] hover:text-[var(--color-foil)] disabled:opacity-60"
      }
    >
      <GlobeIcon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a13 13 0 010 18" />
      <path d="M12 3a13 13 0 000 18" />
    </svg>
  );
}
